import { globalEvent } from '@shared/easyfy.core';
import { addCss, hide, removeCss } from '@shared/easyfy.utils';
import filterService from '@clientWebServices/filter.service';
import { isMobile } from '@clientShared/utils';
import { FilterDOMHelper, FilterSEOHelper, FilterStateHelper, FilterQueryParamsHelper } from '@clientShared/filter.helpers';
import { FilterFor, IFacetGroupCount, ISorter, IUpdatePageParams } from '@clientShared/filter.types';

export class Filter {
  static Instance: Filter;

  FilterQuerySelector: string;
  PagingQuerySelector: string;
  ItemsQuerySelector: string;
  SortersQuerySelector: string;
  SelectedFiltersQuerySelector: string;
  FilterFor: FilterFor;
  Page: number;
  Sorter: ISorter;
  SearchString: string;
  UseHashedUrlFragments: boolean;
  FilterStateHelper: FilterStateHelper;

  constructor(
    filterFor: FilterFor,
    filterQuerySelector: string,
    pagingQuerySelector: string,
    itemsQuerySelector: string,
    sortersQuerySelector: string,
    selectedFiltersQuerySelector: string,
    useHashedUrlFragments: boolean
  ) {
    Filter.Instance = this;
    this.FilterQuerySelector = filterQuerySelector;
    this.PagingQuerySelector = pagingQuerySelector;
    this.ItemsQuerySelector = itemsQuerySelector;
    this.SortersQuerySelector = sortersQuerySelector;
    this.SelectedFiltersQuerySelector = selectedFiltersQuerySelector;
    this.Page = 1;
    this.FilterFor = filterFor;
    this.UseHashedUrlFragments = useHashedUrlFragments;
    this.FilterStateHelper = new FilterStateHelper();

    if (this.UseHashedUrlFragments && this.FilterFor != null && window.location.hash) {
      this.refreshDataOnLoad();
    }

    globalEvent({
      type: 'click',
      events: [
        {
          action: 'load-more-items',
          callback: this.loadMoreItems,
        },
        {
          action: 'clear-filters',
          callback: this.clearFilters,
        },
        {
          action: 'remove-filter',
          callback: this.removeFilter,
        },
        {
          action: 'paging',
          callback: this.paginate,
        },
        {
          callback: this.closeFilterOptionClickOutside,
        },
      ],
    });

    globalEvent({
      type: 'change',
      events: [
        {
          action: 'change-filter',
          callback: this.changeFilter,
        },
        {
          action: 'change-sort',
          callback: this.changeSort,
        },
      ],
    });

    document.addEventListener('change-sort-option', this.changeSort.bind(this));
    this.applyStickyOnScroll();
  }

  private refreshDataOnLoad = () => {
    if (!this.UseHashedUrlFragments) {
      return false;
    }

    this.RefreshData(true, null, true);
  };

  private applyStickyOnScroll = () => {
    const filterContainer = FilterDOMHelper.getFilterHeaderContainer();
    const filterBtn = FilterDOMHelper.getFilterHeaderButton() as HTMLElement;
    const sticky = filterContainer?.offsetTop;
    let hasScrolled: boolean = false;

    document.addEventListener('scroll', () => {
      if (window.scrollY > sticky) {
        hasScrolled = true;
        addCss(filterContainer, 'sticky');
        addCss(filterBtn, ['grid', 'slide-up']);
      } else {
        if (hasScrolled) {
          addCss(filterBtn, 'slide-down');
          removeCss(filterBtn, 'slide-up');
          removeCss(filterContainer, 'sticky');

          setTimeout(function () {
            removeCss(filterBtn, ['grid', 'slide-down']);
            hasScrolled = false;
          }, 500);
        }
      }
    });
  };

  private loadMoreItems = (e: Event, replaceProducts = false) => {
    e.preventDefault();
    e.stopPropagation();

    const button: HTMLElement = FilterDOMHelper.getButtonElement(e.target as HTMLElement);

    Filter.Instance.Page = FilterDOMHelper.getPage(button);
    Filter.Instance.RefreshData(replaceProducts);

    return false;
  };

  private paginate = (e: Event) => {
    this.loadMoreItems(e, true);

    const button: HTMLElement = FilterDOMHelper.getButtonElement(e.target as HTMLElement);
    const totalPages: number = FilterDOMHelper.getTotalPages(button);

    //hide first page components - SEO reason
    FilterSEOHelper.hideFirstPageComponents(Filter.Instance.Page, Filter.Instance.ItemsQuerySelector);
    FilterSEOHelper.updateHeadPagination(Filter.Instance.Page, totalPages);

    window.scrollTo(0, 100); // TODO when design is ready

    return false;
  };

  private changeFilter = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const input: HTMLElement = e.target as HTMLElement;
    const counts: IFacetGroupCount = FilterDOMHelper.getFacetCountsForCurrentGroup(input);

    Filter.Instance.Page = 1;
    Filter.Instance.RefreshData(true, counts);

    return false;
  };

  private removeFilter = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    let el = e.target as HTMLElement;
    if (el.attributes['data-id']?.value == undefined) {
      el = el.parentElement;
    }
    const id = el.attributes['data-id']?.value;
    const input = document.querySelector('input[id="' + id + '"]') as HTMLElement;
    const counts: IFacetGroupCount = FilterDOMHelper.getFacetCountsForCurrentGroup(input);

    input.removeAttribute('checked');
    el.remove();

    Filter.Instance.Page = 1;
    Filter.Instance.RefreshData(true, counts);

    return false;
  };

  private clearFilters = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    let eventElement: HTMLElement = e.target as HTMLElement;
    const pageId = FilterDOMHelper.getPageId();

    FilterDOMHelper.clearSelectedFilters();
    FilterDOMHelper.uncheckAllFilters(Filter.Instance.FilterQuerySelector);

    if (!pageId) return false;

    Filter.Instance.Page = 1;
    Filter.Instance.RefreshData(true);

    if (!eventElement.hasAttribute('data-action')) {
      eventElement = eventElement.parentElement;
    }

    hide(eventElement);

    return false;
  };

  private changeSort = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const selectedElement: HTMLElement = e.target as HTMLElement;

    Filter.Instance.Sorter = FilterDOMHelper.getSortData(selectedElement);
    Filter.Instance.RefreshData(true);
  };

  private closeFilterOptionClickOutside = (e: Event) => {
    const target = e.target as HTMLElement;

    if (!target || isMobile()) return;
    FilterDOMHelper.closeFilterOptionSelector(target);
  };

  private getItems = async (queryParams: any): Promise<any> => {
    const pageId = FilterDOMHelper.getPageId();
    const productListName = FilterDOMHelper.getProductListName(Filter.Instance.ItemsQuerySelector);
    const productListId = FilterDOMHelper.getProductListId(Filter.Instance.ItemsQuerySelector);
    const pathName = window.location.pathname;

    switch (this.FilterFor) {
      case FilterFor.Category:
      case FilterFor.Brand:
      case FilterFor.Search:
        return await filterService.GetProducts(pageId, queryParams, FilterFor[this.FilterFor], productListName, productListId, pathName);
      case FilterFor.Subscription:
        return await filterService.GetSubscriptionProducts(pageId, queryParams, productListName, productListId);
      case FilterFor.Articles:
        return await filterService.GetArticles(pageId, queryParams);
      case FilterFor.Orders:
        return await filterService.GetMoreOrders(queryParams.page);
    }
  };

  RefreshData = (replaceProducts: boolean = true, facetGroupCount: IFacetGroupCount = null, isPageLoad: boolean = false) => {
    Filter.Instance.FilterStateHelper.saveFilterState();

    if (this.UseHashedUrlFragments && isPageLoad) {
      const page = FilterDOMHelper.getPageFromSearchFragment();
      Filter.Instance.Page = page;
    }

    const queryParams = FilterQueryParamsHelper.getQueryParams(
      Filter.Instance.Page,
      Filter.Instance.Sorter,
      this.UseHashedUrlFragments,
      isPageLoad,
      Filter.Instance.FilterQuerySelector,
      facetGroupCount
    );

    const anyFilterChecked = queryParams.checkedFilters?.length > 0;

    this.getItems(queryParams)
      .then((response) => {
        if (response?.status == 200 && response?.data != null && response?.data?.success) {
          var updatePageParams: IUpdatePageParams = {
            responseData: response.data,
            filterQuerySelector: Filter.Instance.FilterQuerySelector,
            pagingQuerySelector: Filter.Instance.PagingQuerySelector,
            itemsQuerySelector: Filter.Instance.ItemsQuerySelector,
            sortersQuerySelector: Filter.Instance.SortersQuerySelector,
            selectedFiltersQuerySelector: Filter.Instance.SelectedFiltersQuerySelector,
            replaceProducts,
          };

          FilterDOMHelper.updatedPageOnFilterChange(updatePageParams);

          if (response.data.pageResource != null) {
            FilterSEOHelper.updateMetaTitle(Filter.Instance.Page, response.data.pageResource);
            FilterSEOHelper.updateMetaCanonical(Filter.Instance.Page);
            FilterSEOHelper.updateMetaAlternate();
          }

          document.dispatchEvent(
            new CustomEvent('filter.totalHitsUpdated', {
              bubbles: true,
              detail: { totalHits: response.data.totalHits },
            })
          );

          Filter.Instance.FilterStateHelper.setFilterState();
          FilterQueryParamsHelper.setQueryParams(queryParams);

          FilterDOMHelper.toggleClearFiltersButton(anyFilterChecked);
          FilterDOMHelper.updateFilterCount();
        } else {
          console.log('Error on refresh data request.');
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };
}
